// @ts-nocheck

import debugLib from 'debug';
import deepMerge from 'deepmerge';
import * as R from 'ramda';

import { reducerWrapper } from '../../support/utils';

import { getPartnerName } from '../../../main/components/PartnerManager/utils';
import {
  CLEAR_SCRAPER_DATA,
  GET_AD_ONTOLOGY,
  GET_ALL_LINKS,
  GET_ANDROID_INFO,
  GET_ENABLED_NETWORKS,
  GET_IOS_INFO,
  GET_LINK,
  GET_TOP_CAMPAIGN,
  GET_TOP_CHANNEL,
  GET_TOP_FEATURE,
  LINK_CREATOR_GET_QLT,
  REMOVE_LINK_DATA_PROPERTY,
  RESET_STORE,
  SAVE_LINK,
  UPDATE_LINK,
  UPDATE_TEMPLATE_VALIDATION_STATUS,
} from '../../actions/LinkCreatorActions';
import { FETCH_LINK_INFO } from '../../actions/ScraperAction';
import { QuickLinkTemplateValidation } from '../../screens/ScreenQuickLinkCreator/QuickLinkTemplates/Validation/QuickLinkTemplateValidation';
import AppsStore from '../../stores/AppsStore_LEGACY';
import { LOADING_STATUS } from '../../support/constants';
import {
  DEFAULT_TEMPLATE_STATE,
  INIT_STATE,
  clearScraperData,
  getLinkTitleKey,
  mergedWithAppConfig,
  mergedWithScraperData,
} from './utils';

const debug = debugLib('LinkCreatorReducer');

const getStateOnSaveLinkError = (state, action) => {
  const loadingStatusObject = {
    loading: false,
    loadingStatus: LOADING_STATUS.ERROR,
  };
  // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
  if (action.error && action.error.status == 409) {
    return Object.assign({}, state, loadingStatusObject, {
      error: {
        message: `The alias "${state.alias}" has already been used. Please try a different one.`,
      },
    });
  } else {
    return Object.assign({}, state, loadingStatusObject, {
      error: action.error,
    });
  }
};

/**
 * For adding ad partner name to channelList when updating data. Added to beginning
 * @param {Object} data - link data to update
 * @param {Array} channelList - list of channels for Select
 * @return link data and include updated channelList if necessary
 */
function updateList(data, channelList = [], campaignList = [], featureList = []) {
  const channel = R.path(['data', '~channel'], data) || data.channel;
  const campaign = R.path(['data', '~campaign'], data) || data.campaign;
  const feature = R.path(['data', '~feature'], data) || data.feature;
  if (channel && !R.find(R.propEq('value', channel), channelList)) {
    channelList = [{ label: channel, value: channel }, ...channelList];
    return {
      ...data,
      channelList,
    };
  }
  if (campaign && !R.find(R.propEq('value', campaign), campaignList)) {
    campaignList = [{ label: campaign, value: campaign }, ...campaignList];
    return {
      ...data,
      campaignList,
    };
  }
  if (feature && !R.find(R.propEq('value', feature), featureList)) {
    featureList = [{ label: feature, value: feature }, ...featureList];
    return {
      ...data,
      featureList,
    };
  }
  return data;
}

function cloneLinkData(linkData = {}) {
  const linkTitleKey = getLinkTitleKey(linkData);
  const linkTitleValue = linkData[linkTitleKey];
  const clonedData = R.merge(linkData, {
    [linkTitleKey]: `${linkTitleValue} - Copy`,
    '~id': undefined,
  });
  if (clonedData['~agency_id']) {
    delete clonedData['~agency_id'];
  }
  return clonedData;
}

function Handler(state = INIT_STATE, action) {
  debug('Receive action type', action.type);
  switch (action.type) {
    // The first four cases are non-async state management cases
    case RESET_STORE:
      return mergedWithAppConfig(INIT_STATE, action.app);

    case CLEAR_SCRAPER_DATA:
      return clearScraperData(state, action.app);

    case UPDATE_LINK:
      const { data = {}, forceUpdate } = action;

      const updatedData = updateList(data, state.channelList, state.campaignList, state.featureList);

      const currentState = { ...state };
      if (updatedData.image && currentState.image) {
        delete currentState.image;
      }

      // forceUpdate is used to do a shallow merge instead of a deepmerge. Use if:
      // 1. you need to replace data object, not merge. (i.e. updating custom link data).
      // 2. you need to prevent merging of nested array contents like tags.
      if (forceUpdate) {
        return R.merge(currentState, updatedData);
      }
      return deepMerge(currentState, updatedData);

    case GET_ALL_LINKS.response:
      const { allLinks } = action.data;
      return Object.assign({}, state, { allLinks });

    case GET_LINK.response:
      const { link = {}, isClone } = action.data;

      const clonedData = isClone ? cloneLinkData(link.data) : link.data;

      return R.mergeAll([
        state,
        link,
        {
          loadingStatus: LOADING_STATUS.LOADED,
          alias: isClone ? undefined : link.alias,
          is_clone: isClone,
          link_id: isClone ? 'new' : link.id || 'new',
          url: isClone ? undefined : link.url,
          id: isClone ? undefined : link.id,
          updateTime: Date.now(),
          data: clonedData,
          ...DEFAULT_TEMPLATE_STATE,
        },
      ]);
    // FETCH_LINK_INFO cases are for fetching scraper data
    case FETCH_LINK_INFO.response:
      return mergedWithScraperData(state, action.data, action.isMarketing);

    case FETCH_LINK_INFO.error:
      if (action.keepExistingUri) {
        return R.merge(state, {
          fetchStatus: LOADING_STATUS.ERROR,
        });
      }

      return R.merge(state, {
        fetchStatus: LOADING_STATUS.ERROR,
        fetchedUrl: action.uri,
      });

    case FETCH_LINK_INFO.loading:
      return R.merge(state, {
        fetchStatus: LOADING_STATUS.LOADING,
        fetchedUrl: action.uri,
      });
    // SAVE_LINK cases are for saving link data
    case SAVE_LINK.response:
      var url = R.path(['data', 'url'], action) || R.path(['data', 'data', '+url'], action);
      if (url.indexOf('http') === -1) {
        url = `http://${url}`;
      }

      const savedLink = {
        ...action.data,
        id: action.data.id,
        link_id: action.data.link_id || action.data.id || 'new',
        updateTime: Date.now(),
        url: url,
        date: action.data.date,
        error: null,
        savedStatus: LOADING_STATUS.LOADED,
        loadingStatus: LOADING_STATUS.LOADED,
      };

      return { ...state, ...savedLink };

    case SAVE_LINK.error:
      return getStateOnSaveLinkError(state, action);
    case 'PROGRESS_BAR_ACTIVE':
      return R.merge(state, {
        savedStatus: LOADING_STATUS.LOADING,
      });
    case 'PROGRESS_BAR_COMPLETE':
      return R.merge(state, {
        savedStatus: LOADING_STATUS.LOADED,
      });
    case 'PROGRESS_BAR_ERROR':
      return R.merge(state, {
        savedStatus: LOADING_STATUS.INIT,
      });
    // The following cases are for fetching data to populate select menus
    case GET_TOP_CHANNEL.response:
      var channel = R.reject(
        R.isNil,
        R.map((item) => {
          if (!R.isNil(item.value)) {
            return {
              value: item.value,
              label: item.value,
            };
          }
        }, action.data)
      );

      const linkDataChannel = state.channel || state.data['~channel'];

      if (linkDataChannel && !R.find(R.propEq('value', linkDataChannel))(channel)) {
        channel.push({
          value: linkDataChannel,
          label: linkDataChannel,
        });
      }
      return R.merge(state, {
        channelList: channel,
      });
    case GET_TOP_CAMPAIGN.response:
      var campaign = R.reject(
        R.isNil,
        R.map((item) => {
          if (!R.isNil(item.value)) {
            return {
              value: item.value,
              label: item.value,
            };
          }
        }, action.data)
      );

      const linkDataCampaign = state.campaign || state.data['~campaign'];

      // if current selected is not in the list
      if (linkDataCampaign && !R.find(R.propEq('value', linkDataCampaign))(campaign)) {
        campaign.push({
          value: linkDataCampaign,
          label: linkDataCampaign,
        });
      }

      return R.merge(state, {
        campaignList: campaign,
      });
    case GET_TOP_FEATURE.response:
      var feature = R.reject(
        R.isNil,
        R.map((item) => {
          if (!R.isNil(item.value)) {
            return {
              value: item.value,
              label: item.value,
            };
          }
        }, action.data)
      );

      const linkDataFeature = state.feature || state.data['~feature'];

      // if current selected is not in the list
      if (linkDataFeature && !R.find(R.propEq('value', linkDataFeature))(feature)) {
        feature.push({
          value: linkDataFeature,
          label: linkDataFeature,
        });
      }

      return R.merge(state, {
        featureList: feature,
      });
    case GET_ENABLED_NETWORKS.response:
      var enabledNetworkList = R.reject(
        R.isNil,
        R.map((item) => {
          return {
            value: item.machine_name,
            label: getPartnerName(item),
            disabled: !item.enabled,
            postbacks: item.postbacks,
            postback_parameters: item.postback_parameters,
            link_only_parameters: item.link_only_parameters,
            status: item.status,
          };
        }, action.data)
      );

      return R.merge(state, {
        enabledNetworkList,
      });
    case GET_AD_ONTOLOGY.response:
      return R.merge(state, { ad_ontology: action.data });
    case GET_ANDROID_INFO.response:
      return R.merge(state, { androidApp: action.data });

    case GET_IOS_INFO:
      return R.merge(state, {
        iosApp: {
          image: action.data.artworkUrl100,
          text: action.data.trackCensoredName,
        },
      });

    case LINK_CREATOR_GET_QLT.request:
      return {
        ...state,
        loadingQlt: true,
      };
    case LINK_CREATOR_GET_QLT.response:
      if (!action || !action.data) {
        return {
          ...state,
          ...DEFAULT_TEMPLATE_STATE,
        };
      }

      const validator = new QuickLinkTemplateValidation(action.data, state);
      return {
        ...state,
        loadingQlt: false,
        quickLinkTemplate: action.data,
        templateValidationMap: validator.getValidationMap(),
      };

    case UPDATE_TEMPLATE_VALIDATION_STATUS:
      const { templateValidationMap } = state;
      if (templateValidationMap[action.property] === undefined) {
        return state;
      }

      return {
        ...state,
        templateValidationMap: {
          ...templateValidationMap,
          [action.property]: action.status,
        },
      };

    case REMOVE_LINK_DATA_PROPERTY:
      const { linkDataKey } = action;
      if (!linkDataKey) {
        return state;
      }

      const newState = { data: { ...state.data } };
      if (linkDataKey in state.data) {
        delete newState.data[linkDataKey];
      }

      const qlt = state.quickLinkTemplate;
      if (qlt) {
        newState.quickLinkTemplate = {
          ...qlt,
          deletedLinkData: [...(qlt.deletedLinkData || []), linkDataKey],
        };
      }

      return {
        ...state,
        ...newState,
      };

    default:
      return mergedWithAppConfig(state, AppsStore.getApp());
  }
}

const LinkCreator = reducerWrapper(INIT_STATE, Handler, {
  request: [
    GET_ENABLED_NETWORKS,
    GET_AD_ONTOLOGY,
    GET_TOP_CHANNEL,
    GET_TOP_CAMPAIGN,
    GET_TOP_FEATURE,
    GET_LINK,
    UPDATE_LINK,
    RESET_STORE,
    CLEAR_SCRAPER_DATA,
    SAVE_LINK,
    LINK_CREATOR_GET_QLT,
  ],
  error: [
    GET_ANDROID_INFO,
    GET_ENABLED_NETWORKS,
    GET_AD_ONTOLOGY,
    GET_TOP_CHANNEL,
    GET_TOP_CAMPAIGN,
    GET_TOP_FEATURE,
    GET_LINK,
    UPDATE_LINK,
    RESET_STORE,
    CLEAR_SCRAPER_DATA,
  ],
  resetOn: ['APP_CHANGE'],
});

export default LinkCreator;
